import { DatePipe } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { ValueFormatterParams } from 'ag-grid-community';
import { GridUtilsService } from './grid-utils.service';

@Injectable({
  providedIn: 'root',
})
export class GridValueFormatterService {
  private datePipe = inject(DatePipe);
  private gridUtils = inject(GridUtilsService);

  private dateTimeFormat = 'yyyy-MM-dd HH:mm';
  private dateFormat = 'yyyy-MM-dd';

  constructor() {}

  /**
   * Retrieves the appropriate value formatter function based on column configuration.
   * @param params - Parameters containing the value to format.
   * @param column - Column configuration object, which may specify a valueFormatter key.
   * @returns Formatted value string based on the column's valueFormatter key.
   */
  getValueFormatter(params: any, column: Record<string, any>): unknown {
    if (column.valueFormatter === 'textFormatter') {
      return this.textFormatter(params);
    } else if (column.valueFormatter === 'intFormatter') {
      return this.floatFormatter(params, 0);
    } else if (column.valueFormatter === 'floatFormatter') {
      return this.floatFormatter(params);
    } else if (column.valueFormatter === 'dateFormatter') {
      return this.dateFormatter(params);
    } else if (column.valueFormatter === 'dateTimeFormatter') {
      return this.dateTimeFormatter(params);
    } else if (column.valueFormatter === 'jsonFormatter') {
      return this.jsonFormatter(params);
    }
  }

  /**
   * Formats a value as a string.
   * @param params - Parameters containing the value to format.
   * @returns Value as a string, or an empty string if value is null.
   */
  textFormatter(params: ValueFormatterParams): string {
    return `${params.value || ''}`;
  }

  /**
   * Formats a numeric value as a decimal with specified decimal places.
   * @param params - Parameters including the numeric value to format.
   * @param decPlaces - Number of decimal places to display (default is 2).
   * @returns Formatted decimal string, or an empty string if value is null.
   */
  floatFormatter(params: ValueFormatterParams, decPlaces: number = 2): string {
    if (params?.value || params?.value === 0) {
      return params.value.toLocaleString(undefined, {
        minimumFractionDigits: decPlaces,
        maximumFractionDigits: decPlaces,
      });
    }
    return '';
  }

  /**
   * Formats a value as a date-only string based on `dateFormat`.
   * @param params - Parameters containing the date value to format.
   * @returns Formatted date string, or null if the date is invalid.
   */
  dateFormatter(params: ValueFormatterParams): string | null {
    const dateObject = this.gridUtils.extractDateFromParams(params.value);
    return this.datePipe.transform(dateObject, this.dateFormat);
  }

  /**
   * Formats a value as a date and time string based on `dateTimeFormat`.
   * @param params - Parameters containing the date value to format.
   * @returns Formatted date and time string, or null if the date is invalid.
   */
  dateTimeFormatter(params: ValueFormatterParams): string | null {
    const dateObject = this.gridUtils.extractDateFromParams(params.value);
    return this.datePipe.transform(dateObject, this.dateTimeFormat);
  }

  /**
   * Formats a value as a JSON string.
   * @param params - Parameters containing the value to format.
   * @returns Formatted JSON string, or an empty string if value is null.
   */
  jsonFormatter(params: ValueFormatterParams): string {
    if (!params?.value) {
      return '';
    }

    try {
      let obj = params.value;

      // If value is a string, try parsing it as JSON
      if (typeof obj === 'string') {
        obj = JSON.parse(obj);
      }

      const formattedJson = JSON.stringify(obj, null, 2);
      return formattedJson;
    } catch (e) {
      // If parsing fails, fallback to just showing the raw string value
      return typeof params.value === 'string'
        ? params.value
        : JSON.stringify(params.value);
    }
  }
}
